<script setup lang="ts">
/**
 * Footer V2 — Selo "Autorizado pelo Ministério da Fazenda"
 *
 * Reutiliza a mesma fonte do footer legado: useLicenses().primarySeal,
 * que vem do JSON assets/json/dynamicLicenses.json. Garante:
 *   - Mesma URL de destino (gov.br/fazenda/.../lista-de-empresas-autorizadas)
 *   - Mesmo asset (autorizado.webp via getAssetsUrlForCDN)
 *   - Mesmo comportamento de tipos (image, base, curacao) — aqui só
 *     renderizamos quando type=image (caso BR, único hoje no JSON).
 */

const { primarySeal, primarySealIsTypeImage } = useLicenses();

// Modo "embedded": usado quando o componente é renderizado dentro de
// outro card (ex: dentro do SealsCompliance no desktop). Nessa variante
// removemos bg/padding/radius próprios para não duplicar o card pai
// e mantemos apenas selo + legenda inline.
withDefaults(
  defineProps<{
    embedded?: boolean;
  }>(),
  { embedded: false },
);

const sealSrc = computed(() => {
  if (!primarySeal.value?.src) return '';

  return getAssetsUrlForCDN(primarySeal.value.src);
});

const sealHref = computed(() => primarySeal.value?.href || '');
const sealAlt = computed(
  () => primarySeal.value?.alt || 'Autorizado pelo Ministério da Fazenda',
);
</script>

<template>
  <div
    v-if="primarySealIsTypeImage"
    class="footer-v2-authorized-seal"
    :class="{ 'footer-v2-authorized-seal--embedded': embedded }"
  >
    <a
      :aria-label="sealAlt"
      class="footer-v2-authorized-seal__link"
      :href="sealHref"
      rel="noopener noreferrer"
      target="_blank"
    >
      <img
        :alt="sealAlt"
        class="footer-v2-authorized-seal__img"
        decoding="async"
        height="50"
        loading="lazy"
        :src="sealSrc"
        width="176"
      />
    </a>

    <p class="footer-v2-authorized-seal__caption">
      Portaria SPA/MF n.º 2090/2024
    </p>
  </div>
</template>

<style scoped lang="scss">
.footer-v2-authorized-seal {
  display: flex;
  padding: 12px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  align-self: stretch;

  border-radius: 9px;
  background: rgba(255, 255, 255, 0.1);

  /* Variante embedded: usada quando renderizado dentro de outro card
     (ex: dentro do SealsCompliance no desktop). Remove bg/padding/radius
     para não duplicar containers; mantém só selo + legenda inline. */
  &--embedded {
    padding: 0;
    background: transparent;
    border-radius: 0;
    align-self: auto;
    flex-shrink: 0;
  }

  &__link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
  }

  &__img {
    display: block;
    width: 176px;
    height: 50px;
    aspect-ratio: 88 / 25;
    max-width: 100%;
    object-fit: contain;

    @media (min-width: 1024px) {
      width: 133px;
      height: 38px;
      aspect-ratio: 133 / 38;
    }
  }

  &__caption {
    margin: 0;
    color: rgba(255, 255, 255, 0.5); // White50
    text-align: center;
    font-family: 'Montserrat', sans-serif;
    font-size: 10px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 15px */
  }
}
</style>
